import { t } from "@lingui/macro";
import { ArrowRightIcon, FileArrowUpIcon, FilePlusIcon, SparkleIcon } from "@phosphor-icons/react";
import { cn } from "@reactive-resume/utils";
import type { ReactNode } from "react";
import { Link } from "react-router";

import { preloadOcrEngine } from "@/client/services/ocr/tesseract";
import { useDialog } from "@/client/stores/dialog";

import { useFormResumeCreate } from "../../dashboard/_components/form-create";
import { USER_SPACE_PATHS } from "../config/navigation";

type EmptyDashboardCardProps = { children: ReactNode; className?: string };

const EmptyDashboardCard = ({ children, className }: EmptyDashboardCardProps) => (
  <article
    className={cn(
      "relative min-h-[220px] overflow-hidden rounded-2xl border transition-all duration-300",
      "hover:-translate-y-0.5 hover:shadow-[0_12px_28px_rgba(15,23,42,0.08)]",
      "sm:min-h-[235px] xl:min-h-[245px]",
      className,
    )}
  >
    {children}
  </article>
);

type ResumeActionButtonProps = {
  title: string;
  description?: string;
  icon: ReactNode;
  onClick: () => void;
  onMouseEnter?: () => void;
};

const ResumeActionButton = ({
  title,
  description,
  icon,
  onClick,
  onMouseEnter,
}: ResumeActionButtonProps) => (
  <button
    type="button"
    onClick={onClick}
    onMouseEnter={onMouseEnter}
    className={cn(
      "group flex min-h-[52px] w-full items-center gap-2.5 rounded-xl border border-slate-200 bg-white px-3 text-left",
      "shadow-[0_4px_12px_rgba(15,23,42,0.05)] transition-all duration-200",
      "hover:-translate-y-0.5 hover:border-indigo-200 hover:shadow-[0_7px_18px_rgba(79,70,229,0.09)]",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 sm:min-h-[56px] sm:px-3.5",
    )}
  >
    <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-indigo-50 text-indigo-600">
      {icon}
    </span>
    <span className="min-w-0">
      <span className="block truncate text-xs font-bold text-indigo-950 sm:text-[13px]">
        {title}
      </span>
      {description && (
        <span className="mt-0.5 block text-[10px] text-slate-500 sm:text-[11px]">
          {description}
        </span>
      )}
    </span>
  </button>
);

export const EmptyDashboard = () => {
  const creationForm = useFormResumeCreate();
  const linkedinDialog = useDialog("import-linkedin");
  const documentDialog = useDialog("import-ocr");
  const { open: openOffers } = useDialog("forfait");

  const handleOpenOffers = () => openOffers("create", { id: "forfait" });

  return (
    <div className="grid gap-3.5 md:grid-cols-2 xl:grid-cols-3">
      <EmptyDashboardCard className="border-cyan-200 bg-gradient-to-br from-cyan-50 via-sky-50 to-cyan-100/70">
        <button
          type="button"
          onClick={() => linkedinDialog.open("create")}
          aria-label="Importer un CV depuis LinkedIn"
          className="group flex min-h-[220px] w-full flex-col p-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-500 sm:min-h-[235px] xl:min-h-[245px]"
        >
          <div className="flex min-h-0 flex-1 items-center justify-center">
            <img
              src="/images/user-space/linkedin-import.png"
              alt=""
              aria-hidden
              className="h-28 w-full object-contain transition-transform duration-500 group-hover:scale-[1.025] sm:h-32 xl:h-36"
            />
          </div>
          <span className="flex min-h-10 w-full items-center justify-center rounded-xl border border-white/80 bg-white/95 px-3 text-center text-xs font-semibold text-indigo-950 shadow-[0_5px_14px_rgba(15,23,42,0.06)] transition-colors group-hover:text-indigo-700">
            Importer depuis LinkedIn
          </span>
        </button>
      </EmptyDashboardCard>

      <EmptyDashboardCard className="border-indigo-200 bg-gradient-to-br from-white via-indigo-50 to-violet-100/70">
        <div className="flex min-h-[220px] flex-col p-3.5 sm:min-h-[235px] sm:p-4 xl:min-h-[245px]">
          <div className="flex items-center gap-2.5">
            <span className="grid size-8 place-items-center rounded-lg bg-indigo-100 text-indigo-700">
              <FilePlusIcon aria-hidden size={18} weight="duotone" />
            </span>
            <h3 className="text-[13px] font-bold text-indigo-950 sm:text-sm">Créer un CV</h3>
          </div>

          <div className="mt-3 flex flex-1 flex-col justify-center gap-2.5">
            <ResumeActionButton
              title="Nouveau CV"
              description="Commencer depuis zéro"
              onClick={() => creationForm?.setformCreateResume(true)}
              icon={<FilePlusIcon aria-hidden size={18} weight="duotone" />}
            />
            <ResumeActionButton
              title={t`Importer un CV`}
              description={t`PDF (.pdf), Word (.docx) ou image (.jpg, .jpeg, .png, .webp)`}
              onClick={() => documentDialog.open("create")}
              onMouseEnter={preloadOcrEngine}
              icon={<FileArrowUpIcon aria-hidden size={18} weight="duotone" />}
            />
            <button
              type="button"
              onClick={handleOpenOffers}
              className={cn(
                "flex min-h-12 w-full items-center justify-between gap-2 rounded-xl bg-gradient-to-r",
                "from-indigo-700 via-violet-700 to-indigo-600 px-4 text-[13px] font-semibold text-white",
                "shadow-[0_8px_18px_rgba(79,70,229,0.2)] transition-all duration-200",
                "hover:-translate-y-0.5 hover:shadow-[0_12px_24px_rgba(79,70,229,0.26)]",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2",
              )}
            >
              <span className="flex min-w-0 items-center gap-2">
                <SparkleIcon aria-hidden size={17} weight="duotone" className="shrink-0" />
                <span className="truncate">Découvrir les offres</span>
              </span>
              <ArrowRightIcon aria-hidden size={17} weight="bold" className="shrink-0" />
            </button>
          </div>
        </div>
      </EmptyDashboardCard>

      <EmptyDashboardCard className="border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-violet-100/70 md:col-span-2 xl:col-span-1">
        <Link
          to={USER_SPACE_PATHS.coverLetters}
          aria-label="Créer une lettre de motivation"
          className="group flex min-h-[220px] flex-col p-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-500 sm:min-h-[235px] xl:min-h-[245px]"
        >
          <div className="flex min-h-0 flex-1 items-center justify-center">
            <img
              src="/images/user-space/cover-letter.png"
              alt=""
              aria-hidden
              className="h-28 w-full object-contain transition-transform duration-500 group-hover:scale-[1.025] sm:h-32 xl:h-36"
            />
          </div>
          <span className="flex min-h-10 w-full items-center justify-center rounded-xl border border-white/80 bg-white/95 px-3 text-center text-xs font-semibold text-indigo-950 shadow-[0_5px_14px_rgba(15,23,42,0.06)] transition-colors group-hover:text-indigo-700">
            Lettre de motivation
          </span>
        </Link>
      </EmptyDashboardCard>
    </div>
  );
};
